import { useCallback, useRef, useState } from "react";
import { deriveDisplayNameFromNames } from "./contact-display-name.js";

export function useDerivedDisplayName(initialDisplayName = ""): {
  firstName: string;
  lastName: string;
  displayName: string;
  setFirstName: (value: string) => void;
  setLastName: (value: string) => void;
  setDisplayName: (value: string) => void;
  reset: (values: {
    firstName?: string;
    lastName?: string;
    displayName?: string;
  }) => void;
} {
  const [firstName, setFirstNameState] = useState("");
  const [lastName, setLastNameState] = useState("");
  const [displayName, setDisplayNameState] = useState(initialDisplayName);
  const displayNameManuallyEditedRef = useRef(false);
  const fallbackDisplayNameRef = useRef(initialDisplayName);

  const applyDerivedDisplayName = useCallback((nextFirstName: string, nextLastName: string) => {
    if (displayNameManuallyEditedRef.current) {
      return;
    }
    const derived = deriveDisplayNameFromNames(nextFirstName, nextLastName);
    setDisplayNameState(derived || fallbackDisplayNameRef.current);
  }, []);

  const setFirstName = useCallback(
    (value: string) => {
      setFirstNameState(value);
      applyDerivedDisplayName(value, lastName);
    },
    [applyDerivedDisplayName, lastName],
  );

  const setLastName = useCallback(
    (value: string) => {
      setLastNameState(value);
      applyDerivedDisplayName(firstName, value);
    },
    [applyDerivedDisplayName, firstName],
  );

  const setDisplayName = useCallback((value: string) => {
    displayNameManuallyEditedRef.current = true;
    setDisplayNameState(value);
  }, []);

  const reset = useCallback(
    (values: { firstName?: string; lastName?: string; displayName?: string }) => {
      displayNameManuallyEditedRef.current = false;
      fallbackDisplayNameRef.current = values.displayName ?? "";
      setFirstNameState(values.firstName ?? "");
      setLastNameState(values.lastName ?? "");
      setDisplayNameState(values.displayName ?? "");
    },
    [],
  );

  return {
    firstName,
    lastName,
    displayName,
    setFirstName,
    setLastName,
    setDisplayName,
    reset,
  };
}
